import { StandardRPCJsonSerializer } from "@orpc/client/standard";
import * as Sentry from "@sentry/tanstackstart-react";
import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { PendingComponent } from "@/components/pending-component";
import { ErrorComponent } from "./components/error-component";
import { NotFound } from "./components/not-found";
import { routeTree } from "./routeTree.gen";

const serializer = new StandardRPCJsonSerializer({
	customJsonSerializers: [],
});

if (typeof document !== "undefined") {
	Sentry.init({
		dsn: "https://0c07486876fc903468d99c5d8ded16f7@o4504813552140288.ingest.us.sentry.io/4511303547813888",
		environment: import.meta.env.VITE_ENVIRONMENT || "local",
		release: import.meta.env.VITE_VERSION,
		enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",

		ignoreErrors: ["Skipped ViewTransition", "Hydration failed", "The operation was aborted."],

		integrations: [],

		// Set tracesSampleRate to 1.0 to capture 100%
		// of transactions for tracing.
		tracesSampleRate: 1.0,

		// Set profilesSampleRate to 1.0 to profile every transaction.
		// Since profilesSampleRate is relative to tracesSampleRate,
		// the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
		// For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
		// results in 25% of transactions being profiled (0.5*0.5=0.25)
		profilesSampleRate: 1.0,

		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,
	});
}

// Create a new router instance
export const getRouter = () => {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				queryKeyHashFn(queryKey) {
					const [json, meta] = serializer.serialize(queryKey);

					return JSON.stringify({ json, meta });
				},
				retry: false,
				staleTime: 60 * 1000, // > 0 to prevent immediate refetching on mount
			},
			dehydrate: {
				serializeData(data) {
					const [json, meta] = serializer.serialize(data);

					return { json, meta };
				},
			},
			hydrate: {
				deserializeData(data) {
					return serializer.deserialize(data.json, data.meta);
				},
			},
		},
	});

	const router = createRouter({
		routeTree,
		context: {
			queryClient,
		},
		defaultErrorComponent: ErrorComponent,
		defaultNotFoundComponent: () => <NotFound />,
		defaultPreload: "intent",
		defaultPendingComponent: PendingComponent,
		scrollRestoration: true,
	});

	if (!router.isServer) {
		Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));
	}

	setupRouterSsrQueryIntegration({
		router,
		queryClient,
	});

	return router;
};
